import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { login } from '../api/authService';
import AppleAuthButton from '../components/AppleAuthButton';
import { AuthContext } from '../context/AuthContext';
import { useNativeCaps } from '../context/NativeCapsContext';
import { useLocale } from '../context/LocaleContext';
import config from '../config/config';
import { canUseNativeAppleAuth, getAuthQueryErrorMessage } from '../utils/appleAuth';
import { LOGIN_NOTICE_STORAGE_KEY } from '../utils/aiDataProcessingConsent';
import { detectBrowserTimezone } from '../utils/timezone';

const LoginPage = () => {
  const navigate = useNavigate();
  const { isNativeApp, caps, postToNative } = useNativeCaps();
  const { t } = useLocale();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const { setUser } = useContext(AuthContext);
  const canShowAppleAuth = canUseNativeAppleAuth({ isNativeApp, caps });

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const authError = getAuthQueryErrorMessage(queryParams.get('error'), t);
    if (authError) {
      setError(authError);
    }
    const loginNotice = sessionStorage.getItem(LOGIN_NOTICE_STORAGE_KEY);
    if (loginNotice) {
      setNotice(loginNotice);
      sessionStorage.removeItem(LOGIN_NOTICE_STORAGE_KEY);
    }
  }, [t]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const timezone = detectBrowserTimezone();
      const data = await login(email, password, timezone || undefined);
      setUser(data.user);
      navigate('/dashboard'); // Redirect to dashboard after successful login
    } catch (err) {
      if (err.response && err.response.data && err.response.data.msg) {
        setError(err.response.data.msg);
      } else {
        setError(t('auth.unexpectedError', 'Something went wrong. Please try again.'));
      }
    }
  };

  const handleAppleLogin = () => {
    setError('');
    const posted = postToNative({
      type: 'FORZA_APPLE_AUTH',
      action: 'login',
    });

    if (!posted) {
      setError(t('auth.appleUnavailable', 'Apple Sign In is unavailable in this environment.'));
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0B0F19] via-[#111827] to-[#0A192F] text-white px-4 relative overflow-hidden">
      {/* Deep Glassmorphism Ambient Glows */}
      <div className="absolute top-[-15%] left-[-10%] w-[50%] h-[50%] bg-cyan-600/20 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-[-15%] right-[-10%] w-[50%] h-[50%] bg-blue-600/20 rounded-full blur-[120px] pointer-events-none" />

      <div className="relative z-10 max-w-md w-full space-y-8 bg-slate-900/60 p-10 rounded-[2rem] border border-white/10 shadow-2xl backdrop-blur-2xl">
        <div className="text-center">
          <h1 className="text-4xl font-extrabold tracking-tight text-white mb-3">
            {t('auth.loginTitle', 'Welcome back')}
          </h1>
          <p className="text-gray-400 font-medium tracking-wide">
            {t('auth.loginSubtitle', 'Pick up where you left off.')}
          </p>
        </div>

        <form onSubmit={handleLogin} className="mt-8 space-y-6">
          <div className="space-y-4">
            <div>
              <label htmlFor="email-address" className="sr-only">{t('auth.email', 'Email address')}</label>
              <input
                id="email-address"
                name="email"
                type="email"
                inputMode="email"
                autoComplete="username"
                required
                className="relative block w-full rounded-xl border border-white/10 bg-slate-950/50 px-4 py-4 text-white placeholder-gray-500 focus:border-cyan-400 focus:outline-none focus:ring-1 focus:ring-cyan-400 sm:text-sm transition-all shadow-inner backdrop-blur-sm"
                placeholder={t('auth.email', 'Email address')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="password" className="sr-only">{t('auth.password', 'Password')}</label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                className="relative block w-full rounded-xl border border-white/10 bg-slate-950/50 px-4 py-4 text-white placeholder-gray-500 focus:border-cyan-400 focus:outline-none focus:ring-1 focus:ring-cyan-400 sm:text-sm transition-all shadow-inner backdrop-blur-sm"
                placeholder={t('auth.password', 'Password')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
          </div>

          {error && (
            <div className="rounded-xl bg-red-950/50 p-4 border border-red-900/50 backdrop-blur-sm">
              <div className="flex items-center">
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-200">
                    {error}
                  </h3>
                </div>
              </div>
            </div>
          )}

          {notice && (
            <div className="rounded-xl border border-cyan-500/20 bg-cyan-950/40 p-4 backdrop-blur-sm">
              <div className="flex items-center">
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-cyan-100">
                    {notice}
                  </h3>
                </div>
              </div>
            </div>
          )}

          <div>
            <button
              type="submit"
              className="group relative flex w-full justify-center rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 px-4 py-4 text-sm font-bold text-white hover:from-cyan-400 hover:to-blue-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900 transition-all shadow-lg hover:shadow-cyan-500/25 active:scale-[0.98]"
            >
              {t('auth.signIn', 'Sign in')}
            </button>
          </div>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/10" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="bg-slate-900/60 px-4 text-gray-400 backdrop-blur-sm rounded-full">{t('auth.continueWith', 'Or continue with')}</span>
            </div>
          </div>

          <div>
            {canShowAppleAuth ? (
              <AppleAuthButton
                label={t('auth.signInWithApple', 'Sign in with Apple')}
                onClick={handleAppleLogin}
                className="mb-3"
              />
            ) : null}

            <button
              type="button"
              onClick={() => window.location.href = `${config.api.baseUrl}/users/auth/google?action=login`}
              className="flex w-full justify-center items-center gap-3 rounded-xl border border-white/10 bg-white/5 px-4 py-4 text-sm font-semibold text-white hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/20 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900 transition-all backdrop-blur-sm active:scale-[0.98]"
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24">
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.26-.19-.58z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
              {t('auth.signInWithGoogle', 'Sign in with Google')}
            </button>
          </div>

          <div className="text-center pt-2">
            <button
              type="button"
              onClick={() => navigate('/register')}
              className="text-sm font-semibold text-cyan-400 hover:text-cyan-300 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 rounded-md px-2 py-1"
            >
              {t('auth.createAccount', 'New here? Create your account')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LoginPage;
